import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaWhatsapp, FaArrowLeft, FaStar, FaUserCircle } from 'react-icons/fa';
import { ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import ShareProfileModal from './ShareProfileModal';
import SignupModal from './SignupModal';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Spinner from './Spinner';
import defaultProfile from '../assets/default-profile.png';
import Header from './Header';
import Footer from './Footer';


/**
 * Professional Profile Page
 * Shows professional details, availability, booking, and share options.
 * Route: /professional/:id
 */
const ProfilePage = () => {
  const { id } = useParams();
  const location = useLocation();
  const passedPhotoURL = location.state?.photoURL || null;
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRequestingSlots, setIsRequestingSlots] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [activeTab, setActiveTab] = useState('About'); // About | Availability | Services | Reviews
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingDateTime, setBookingDateTime] = useState('');
  const [bookingNote, setBookingNote] = useState('');
  const [sendingBooking, setSendingBooking] = useState(false);
  const db = getFirestore();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const functions = getFunctions();

  useEffect(() => {
    // Fetch professional profile by ID and reviews
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch professional profile
        const profRef = doc(db, 'professionals', id);
        const profSnap = await getDoc(profRef);
        
        if (profSnap.exists()) {
          setProfile({ id, ...profSnap.data() });
          
          // Fetch reviews
          const reviewsRef = collection(db, 'professionals', id, 'reviews');
          const q = query(reviewsRef, orderBy('reviewDate', 'desc'), limit(20));
          const querySnapshot = await getDocs(q);
          
          const reviewsData = [];
          querySnapshot.forEach((doc) => {
            reviewsData.push({ id: doc.id, ...doc.data() });
          });
          
          setReviews(reviewsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load profile data');
      }
      setLoading(false);
    };
    
    fetchData();
  }, [id, db]);

  // Send a booking invite email to professional, CCing info@asdflo.com
  const handleSendBookingInvite = async () => {
    try {
      if (!currentUser) {
        setShowLoginModal(true);
        return;
      }
      if (!bookingDateTime) {
        toast.error('Please select a date & time');
        return;
      }
      setSendingBooking(true);
      const sendEmailFn = httpsCallable(functions, 'sendEmail');
      const profName = profile.fullName || profile.name || 'Professional';
      const profEmail = profile.email || 'info@asdflo.com';
      const requesterName = currentUser.displayName || 'ASDflo user';
      const when = new Date(bookingDateTime);
      const whenText = isNaN(when.getTime()) ? bookingDateTime : when.toLocaleString();
      const cc = ['asdfloteam@gmail.com'];

      const subject = `Booking request for ${profName}`;
      const text = `Hello ${profName},\n\n` +
        `${requesterName} requests a meeting.\n` +
        `Preferred date & time: ${whenText}\n` +
        `${bookingNote ? `Notes: ${bookingNote}\n` : ''}` +
        `Profile: ${window.location.href}\n\n` +
        `Please confirm availability or propose alternatives.\n\nThanks,\nASDflo`;

      const html = `
        <div style="font-family:Arial,sans-serif;line-height:1.6;color:#111">
          <p>Hello <strong>${profName}</strong>,</p>
          <p><strong>${requesterName}</strong> requests a meeting.</p>
          <p><strong>Preferred date & time:</strong> ${whenText}</p>
          ${bookingNote ? `<p><strong>Notes:</strong> ${bookingNote.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</p>` : ''}
          <p>Profile: <a href="${window.location.href}" target="_blank" rel="noopener">${window.location.href}</a></p>
          <p>Please confirm availability or propose alternatives.</p>
          <p style="margin-top:16px">Thanks,<br/>ASDflo</p>
        </div>`;

      const to = Array.isArray(profEmail) ? profEmail : [profEmail];
      await sendEmailFn({ to, cc, subject, text, html, eventType: 'booking_request' });
      toast.success('Booking request sent');
      setShowBookingModal(false);
      setBookingDateTime('');
      setBookingNote('');
    } catch (err) {
      console.error('Error sending booking invite:', err);
      toast.error('Failed to send booking request');
    } finally {
      setSendingBooking(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );

  if (!profile) return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Professional Not Found</h2>
          <p className="text-gray-600 mb-6">The requested profile could not be found or may have been removed.</p>
          <button 
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
          >
            <FaArrowLeft className="mr-2" /> Go Back
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );

  const handleShare = (type) => {
    const url = window.location.href;
    const text = `Check out this professional on ASDFlo: ${profile.name}`;
    if (type === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`);
    } else if (type === 'email') {
      window.open(`mailto:?subject=ASDFlo Professional Profile&body=${encodeURIComponent(text + '\n' + url)}`);
    } else if (type === 'copy') {
      navigator.clipboard.writeText(url);
      alert('Profile link copied!');
    }
  };

  const handleAskForSlots = async () => {
    try {
      if (!currentUser) {
        setShowLoginModal(true);
        return;
      }

      setIsRequestingSlots(true);
      const sendEmailFn = httpsCallable(functions, 'sendEmail');
      const profName = profile.fullName || profile.name || 'Professional';
      const profEmail = profile.email || 'info@asdflo.com';
      const requesterName = currentUser.displayName || 'ASDflo user';
      const requesterEmail = currentUser.email || 'unknown';
      const profileUrl = window.location.href;

      const subject = `Availability request for ${profName}`;
      const text = `Hello ${profName},\n\n` +
        `${requesterName} (${requesterEmail}) is requesting your available slots for a session.\n` +
        `Profile: ${profileUrl}\n` +
        `Please reply with your available dates/times and session details.\n\n` +
        `Thanks,\nASDflo`;

      const html = `
        <div style=\"font-family:Arial,sans-serif;line-height:1.6;color:#111\">
          <p>Hello <strong>${profName}</strong>,</p>
          <p><strong>${requesterName}</strong> (<a href=\"mailto:${requesterEmail}\">${requesterEmail}</a>) is requesting your available slots for a session.</p>
          <p>Profile: <a href=\"${profileUrl}\" target=\"_blank\" rel=\"noopener\">${profileUrl}</a></p>
          <p>Please reply with your available dates/times and session details.</p>
          <p style=\"margin-top:16px\">Thanks,<br/>ASDflo</p>
        </div>`;

      const to = Array.isArray(profEmail) ? profEmail : [profEmail];
      await sendEmailFn({ to, replyTo: requesterEmail, subject, text, html, eventType: 'availability_request' });
      toast.success('Request sent. The professional will share available slots.');
    } catch (err) {
      console.error('Error requesting slots:', err);
      toast.error('Failed to send slot request');
    } finally {
      setIsRequestingSlots(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    
    if (!reviewText.trim()) {
      toast.error('Please enter your review');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const reviewData = {
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Anonymous',
        userEmail: currentUser.email,
        reviewText: reviewText.trim(),
        rating,
        reviewDate: serverTimestamp(),
        status: 'pending' // For moderation if needed
      };
      
      // Add review to subcollection
      await addDoc(collection(db, 'professionals', id, 'reviews'), reviewData);
      
      // Update local state
      setReviews([
        {
          ...reviewData,
          id: 'temp-' + Date.now(),
          reviewDate: { seconds: Math.floor(Date.now() / 1000) }
        },
        ...reviews
      ]);
      
      setReviewText('');
      setRating(5);
      toast.success('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return 'Just now';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <FaStar 
        key={i} 
        className={`${i < rating ? 'text-yellow-400' : 'text-gray-300'} inline-block`} 
      />
    ));
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      <Header />
      <main className="flex-grow py-8 px-4">
        <div className="max-w-6xl mx-auto">
          

          {/* Main Content with Sticky Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8 order-2 lg:order-1">
              {/* Hero with About Section */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 border border-gray-100">
                <div className="flex flex-col gap-3 max-w-2xl">
                  <div>
                    <p className="font-semibold text-gray-900 whitespace-pre-line">{profile.bio || 'No bio available.'}</p>
                  </div>
                </div>
              </div>
            {/* Services Section */}
            <section className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Services</h2>
              <div className="flex flex-wrap gap-2">
                {profile.services?.map ? (
                  profile.services.map(service => (
                    <span key={service} className="px-4 py-2 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-lg">
                      {service}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-500">No services listed yet.</p>
                )}
              </div>
            </section>

            {/* Availability Section */}
            <section className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Availability</h2>
              {profile.availability && Object.entries(profile.availability).some(([_, d]) => d && d.available) ? (
                <div className="space-y-4">
                  {Object.entries(profile.availability).map(([day, data]) => {
                    if (!data?.available) return null;
                    return (
                      <div key={day} className="mb-4">
                        <h3 className="font-medium text-gray-800 capitalize">{day}</h3>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {Array.isArray(data.hours) && data.hours.length > 0 ? (
                            data.hours.map((slot, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1.5 bg-green-50 text-green-700 text-sm font-medium rounded-lg border border-green-100"
                              >
                                {slot.start} - {slot.end}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No specific hours set</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-start gap-4">
                  <p className="text-gray-600">Ask for available slots from this professional.</p>
                  <button
                    onClick={handleAskForSlots}
                    disabled={isRequestingSlots}
                    className={`px-6 py-2 rounded-lg text-white font-medium ${
                      isRequestingSlots ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                    } transition-colors shadow-md`}
                  >
                    {isRequestingSlots ? 'Sending request...' : 'Ask for Available Slots'}
                  </button>
                </div>
              )}
            </section>

            {/* Reviews Section */}
            <section className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100">Reviews</h2>
              
              {/* Review Form */}
              {currentUser ? (
                <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-800 mb-3">Write a Review</h3>
                  <div className="flex items-center mb-3">
                    <span className="text-gray-600 mr-2">Rating:</span>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          className="text-2xl focus:outline-none"
                        >
                          {star <= rating ? '★' : '☆'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Share your experience..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows="3"
                  />
                  <button
                    onClick={handleReviewSubmit}
                    disabled={isSubmitting}
                    className={`mt-2 px-4 py-2 rounded-lg text-white font-medium ${
                      isSubmitting ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                    } transition-colors`}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-blue-50 text-blue-700 rounded-lg">
                  <p>Please sign in to leave a review.</p>
                </div>
              )}
              
              {/* Reviews List */}
              <div className="space-y-6">
                {reviews.length > 0 ? (
                  reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
                      <div className="flex items-center mb-2">
                        <FaUserCircle className="text-gray-400 text-3xl mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900">{review.userName}</p>
                          <div className="flex items-center mt-1">
                            <div className="flex">
                              {renderStars(review.rating)}
                            </div>
                            <span className="ml-2 text-sm text-gray-500">{formatDate(review.reviewDate)}</span>
                          </div>
                          <p className="text-gray-700 mt-2">{review.reviewText}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No reviews yet. Be the first to review!</p>
                )}
              </div>
            </section>

            {/* Expertise Section */}
            {Array.isArray(profile.expertise) && profile.expertise.length > 0 && (
              <section className="bg-white rounded-2xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Expertise</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.expertise.map((skill, i) => (
                    <span 
                      key={i} 
                      className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-lg"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </section>
            )}
            </div>

            {/* Sticky Sidebar Card */}
            <aside className="lg:col-span-1 self-start lg:sticky top-24 order-1 lg:order-2">
              <div className="bg-white rounded-2xl shadow-md p-5 border border-gray-100">
                {/* Mobile header image (background) */}
                <div className="block lg:hidden relative w-full h-44 rounded-2xl overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-top"
                    style={{ backgroundImage: `url(${passedPhotoURL || profile.photoURL || defaultProfile})`, backgroundPosition: 'center top' }}
                  />
                  <div className="absolute top-3 right-3 flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => setShowShareModal(true)}
                      className="p-2 rounded-lg bg-white/80 backdrop-blur hover:bg-white text-gray-700 shadow"
                      aria-label="Share profile"
                      title="Share profile"
                    >
                      <ArrowUpOnSquareIcon className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleShare('whatsapp')}
                      className="p-2 rounded-lg bg-white/80 backdrop-blur hover:bg-white text-green-700 shadow"
                      aria-label="WhatsApp"
                      title="WhatsApp"
                    >
                      <FaWhatsapp className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="hidden lg:flex items-start gap-3 justify-center">
                  <img
                    src={passedPhotoURL || profile.photoURL || defaultProfile}
                    alt={profile.fullName || 'Professional'}
                    className="w-28 h-28 rounded-2xl object-cover border-4 border-indigo-100 shadow-sm"
                  />
                  <div className="flex flex-col gap-2 ml-1">
                    <button
                      type="button"
                      onClick={() => setShowShareModal(true)}
                      className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                      aria-label="Share profile"
                      title="Share profile"
                    >
                      <ArrowUpOnSquareIcon className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleShare('whatsapp')}
                      className="p-1 text-green-600 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 rounded"
                      aria-label="WhatsApp"
                      title="WhatsApp"
                    >
                      <FaWhatsapp className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 text-center flex flex-col items-center gap-1.5">
                  <div className="flex items-baseline justify-center gap-2">
                    <p className="text-2xl font-bold text-gray-900 leading-tight">{profile.fullName}</p>
                    {profile.city && (
                      <p className="text-sm text-gray-600 leading-tight">📍 {profile.city}</p>
                    )}
                  </div>
                  {(() => {
                    const collectFromObj = (obj) => {
                      if (!obj || Array.isArray(obj) || typeof obj !== 'object') return [];
                      return Object.keys(obj).filter(k => !!obj[k] && obj[k] !== 'false');
                    };
                    const fromCommaStr = (val) => (typeof val === 'string' ? val.split(',').map(s => s.trim()).filter(Boolean) : []);
                    let titles = [];
                    if (profile.title) titles = titles.concat(fromCommaStr(profile.title));
                    if (profile.designation) titles = titles.concat(fromCommaStr(profile.designation));
                    if (profile.profession) titles = titles.concat(fromCommaStr(profile.profession));
                    if (Array.isArray(profile.expertise)) titles = titles.concat(profile.expertise);
                    else if (typeof profile.expertise === 'string') titles = titles.concat(fromCommaStr(profile.expertise));
                    else titles = titles.concat(collectFromObj(profile.expertise));
                    const uniq = Array.from(new Set(titles.filter(Boolean).map(t => String(t))));
                    const toTitle = (s) => s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
                    const text = (uniq.length ? uniq : [profile.title || profile.profession || 'Professional']).slice(0,3).map(toTitle).join(' • ');
                    return <p className="text-gray-700 leading-tight">{text}</p>;
                  })()}
                  {(() => {
                    const ratings = reviews.map(r => Number(r.rating) || 0);
                    const avg = ratings.length ? (ratings.reduce((a,b)=>a+b,0)/ratings.length) : null;
                    return avg ? (
                      <div className="flex items-center text-sm text-gray-700 mt-1">
                        <span className="text-yellow-500 mr-1">★</span>
                        {avg.toFixed(1)}
                        <span className="ml-2 text-gray-500">({ratings.length} reviews)</span>
                      </div>
                    ) : null;
                  })()}
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Fees</span>
                    <span className="font-semibold text-gray-900">
                      {(() => {
                        const fee = profile.fee;
                        const type = profile.feeType;
                        if (fee == null || fee === '') return 'Contact';
                        return `₹${fee}${type ? ` / ${type}` : ''}`;
                      })()}
                    </span>
                  </div>
                  {profile.responseTime && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Response</span>
                      <span className="font-semibold text-gray-900">{profile.responseTime}</span>
                    </div>
                  )}
                  {profile.studentsCount && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Students</span>
                      <span className="font-semibold text-gray-900">{profile.studentsCount}+</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (!currentUser) { setShowLoginModal(true); return; }
                    setShowBookingModal(true);
                  }}
                  className="mt-5 w-full px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow"
                >
                  Contact
                </button>
              </div>
            </aside>
          </div>
        </div>
      </main>
      
      {/* Share Profile Modal */}
      <ShareProfileModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        profile={profile}
      />
      
      {/* Signup Modal */}
      <SignupModal 
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Book a Meeting</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred date & time</label>
            <input
              type="datetime-local"
              value={bookingDateTime}
              onChange={(e) => setBookingDateTime(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 mb-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={bookingNote}
              onChange={(e) => setBookingNote(e.target.value)}
              rows="3"
              placeholder="Add any context or preferences..."
              className="w-full border border-gray-300 rounded-lg p-2 mb-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowBookingModal(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendBookingInvite}
                disabled={sendingBooking}
                className={`px-4 py-2 rounded-lg text-white ${sendingBooking ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} shadow`}
              >
                {sendingBooking ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ProfilePage;
